import fuzzysort from 'fuzzysort';
import {isUndefined} from 'lodash';
import React from 'react';
import {Selector} from 'react-redux';

import {
  fuzzySortBaseOptions,
  maxSearchSuggestions,
} from '../../../../../../srcCommon/inboxes/search/searchHelpers';
import {useAppSelector} from '../../../../../helpers/reduxHelpers';
import {AppSelector} from '../../../../../helpers/reselectHelpers';
import {AppState} from '../../../../../store/appState';
import {FuzzyHighlight} from '../../../cells/header/search/highlight/fuzzyHighlight';
import {
  InboxesSearchSuggestionItemProps,
  InboxesSearchSuggestionsBaseProps,
  InboxesSearchSuggestionsResourceProps,
  InboxesSuggestionsDropdownSection,
  InboxesSuggestionsDropdownSectionTitle,
  InboxesSuggestionsDropdownSectionWrapper,
  renderResourceItem,
} from './inboxesSearchSuggestionsDropdownCommons';

/*
 * Props.
 */

interface InboxesSearchSuggestionsDropdownResourceStoreProps<T>
  extends InboxesSearchSuggestionsBaseProps,
    InboxesSearchSuggestionsResourceProps<T> {
  itemsSelector: AppSelector<ReadonlyArray<T>> | Selector<AppState, ReadonlyArray<T>>;
}

/*
 * Component.
 */

export const InboxesSearchSuggestionsDropdownResourceStore = <T,>(
  props: InboxesSearchSuggestionsDropdownResourceStoreProps<T>,
) => {
  const {itemsSelector, sectionTitle, activeToken} = props;

  const storeItems = useAppSelector(itemsSelector);

  const items = searchItems(storeItems, props);

  if (items.length === 0) {
    return null;
  }

  return (
    <InboxesSuggestionsDropdownSectionWrapper>
      <InboxesSuggestionsDropdownSection>
        <InboxesSuggestionsDropdownSectionTitle>{sectionTitle}</InboxesSuggestionsDropdownSectionTitle>
        {items.map((item) => renderResourceItem(item, props, activeToken.modifier))}
      </InboxesSuggestionsDropdownSection>
    </InboxesSuggestionsDropdownSectionWrapper>
  );
};

/*
 * Helpers.
 */

function searchItems<T>(
  storeItems: ReadonlyArray<T>,
  props: InboxesSearchSuggestionsDropdownResourceStoreProps<T>,
): ReadonlyArray<T & InboxesSearchSuggestionItemProps> {
  const {activeToken, modifiers, valueKey, primaryKey, secondaryKey} = props;

  // Return empty array when search conditions are not found
  if (isUndefined(activeToken.modifier) || !storeItems || !modifiers.includes(activeToken.modifier)) {
    return [];
  }

  // Return default items if value is empty
  if (isUndefined(activeToken.value) || !activeToken.value.length) {
    return storeItems.slice(0, maxSearchSuggestions).map((item) => ({
      ...item,
      value: `${activeToken.modifier}:${item[valueKey]}`,
      primaryLabel: item[primaryKey],
      secondaryLabel: item[secondaryKey || valueKey],
    }));
  }

  // Otherwise, search in store
  return fuzzysort
    .go(activeToken.value, [...storeItems], {
      ...fuzzySortBaseOptions,
      keys: [String(primaryKey), String(secondaryKey)],
    })
    .map((item) => ({
      ...item.obj,
      value: `${activeToken.modifier}:${item.obj[valueKey]}`,
      primaryLabel: <FuzzyHighlight fuzzy={item[0]} text={String(item.obj[primaryKey])} />,
      secondaryLabel: <FuzzyHighlight fuzzy={item[1]} text={String(item.obj[secondaryKey || valueKey])} />,
    }));
}
